// src/components/ui/MagnitudeSelector.js
import React from 'react';

/**
 * Reusable Magnitude Selector Component
 * Allows users to switch between Units, Thousands (K), and Millions (M)
 * for easier input of large numbers
 */
const MagnitudeSelector = ({ value, onChange, className = '' }) => {
    const magnitudes = [
        { value: 'units', label: 'Units', shortLabel: '' },
        { value: 'thousands', label: 'Thousands', shortLabel: 'K' },
        { value: 'millions', label: 'Millions', shortLabel: 'M' }
    ];

    return (
        <div className={className}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
                Display Values In
            </label>
            <div className="flex space-x-2">
                {magnitudes.map((magnitude) => (
                    <button
                        key={magnitude.value}
                        type="button"
                        onClick={() => onChange(magnitude.value)}
                        className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                            value === magnitude.value
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        {magnitude.label} {magnitude.shortLabel && `(${magnitude.shortLabel})`}
                    </button>
                ))}
            </div>
        </div>
    );
};

/**
 * Magnitude Utilities Hook
 * Provides helper functions for working with magnitude values
 */
export const useMagnitude = (magnitude = 'millions') => {
    // Convert magnitude string to multiplier
    const getMultiplier = () => {
        switch (magnitude) {
            case 'thousands':
                return 1000;
            case 'millions':
                return 1000000;
            case 'units':
            default:
                return 1;
        }
    };

    // Get the label (K, M, or empty)
    const getLabel = () => {
        switch (magnitude) {
            case 'thousands':
                return 'K';
            case 'millions':
                return 'M';
            case 'units':
            default:
                return '';
        }
    };

    // Get placeholder text based on magnitude
    const getPlaceholder = (baseValue) => {
        switch (magnitude) {
            case 'millions':
                return `e.g., ${(baseValue / 1000000).toFixed(1)}`;
            case 'thousands':
                return `e.g., ${(baseValue / 1000).toFixed(0)}`;
            case 'units':
            default:
                return `e.g., ${baseValue}`;
        }
    };

    // Get step value for inputs
    const getStepValue = () => {
        switch (magnitude) {
            case 'millions':
                return '0.1';
            case 'thousands':
                return '10';
            case 'units':
            default:
                return '100000';
        }
    };

    // Convert input value to actual amount
    const toActualValue = (inputValue) => {
        const parsed = parseFloat(inputValue);
        if (isNaN(parsed) || !parsed) return 0;
        return parsed * getMultiplier();
    };

    // Convert actual value to display value
    const toDisplayValue = (actualValue) => {
        if (!actualValue && actualValue !== 0) return '';
        const multiplier = getMultiplier();
        return multiplier > 1 ? actualValue / multiplier : actualValue;
    };

    // Format currency with magnitude suffix (e.g., $5.00M)
    const formatCurrencyWithMagnitude = (value) => {
        if (!value && value !== 0) return '$0';
        
        const absValue = Math.abs(value);
        
        if (absValue >= 1000000) {
            // Millions
            return `$${(value / 1000000).toFixed(2)}M`;
        } else if (absValue >= 1000) {
            // Thousands
            return `$${(value / 1000).toFixed(1)}K`;
        } else {
            // Units
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(value);
        }
    };

    // Format currency full amount (no magnitude)
    const formatCurrency = (value) => {
        if (!value && value !== 0) return '$0';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    return {
        multiplier: getMultiplier(),
        label: getLabel(),
        getPlaceholder,
        getStepValue,
        toActualValue,
        toDisplayValue,
        formatCurrencyWithMagnitude,
        formatCurrency
    };
};

export default MagnitudeSelector;











